import { useStatData } from "src/context/StatContext";
import PercentageChangePieChart from "../components/PercentageChangePieChart";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { Paper, Typography } from "@mui/material";
import { useDarkModeData } from "src/context/DarkModeContext";
import PropTypes from "prop-types";

const COLORS_for_dark_theme = [
  "#FF6347", // Tomato
  "#32CD32", // Lime Green
  "#1E90FF", // Dodger Blue
  "#FFD700", // Gold
  "#FF69B4", // Hot Pink
  "#00FA9A", // Medium Spring Green
  "#FF4500", // Orange Red
  "#7B68EE", // Medium Slate Blue
];

const COLORS_for_light_theme = [
  "#8B0000", // Dark Red
  "#006400", // Dark Green
  "#00008B", // Dark Blue
  "#B8860B", // Dark Goldenrod
  "#8B008B", // Dark Magenta
  "#2F4F4F", // Dark Slate Gray
  "#FF8C00", // Dark Orange
  "#4B0082", // Indigo
];

const transformData = (data) => {
  const result = data.reduce((acc, item) => {
    const { betoniAsiakasNimi, m3 } = item;
    if (!acc[betoniAsiakasNimi]) {
      acc[betoniAsiakasNimi] = 0;
    }
    acc[betoniAsiakasNimi] += m3;
    return acc;
  }, {});

  return Object.keys(result).map((key) => ({
    name: key,
    value: result[key],
  }));
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <Paper className="custom-tooltip" style={{ border: "1px solid #ccc", padding: "5px" }}>
        <Typography>{`Kuukausi: ${new Date(2024, label - 1).toLocaleString("fi", {
          month: "long",
        })}`}</Typography>
        {payload.map((entry, index) => (
          <Typography key={`item-${index}`} style={{ color: entry.color }}>
            {`${entry.name}: ${entry.value}`}
          </Typography>
        ))}
      </Paper>
    );
  }

  return null;
};
CustomTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
  label: PropTypes.string,
};

export default function Stat_Dashboard_TopGraphs() {
  const { data, selectedMonthData, selectedMonthData2, comparingMonthData, comparingMonthsCount } =
    useStatData();

  const { darkMode } = useDarkModeData();
  const pieData = transformData(selectedMonthData2);
  const COLORS = darkMode ? COLORS_for_dark_theme : COLORS_for_light_theme;

  return (
    <div style={{ width: "100%" }}>
      <div className="center" style={{ flexWrap: "wrap" }}>
        <Paper
          elevation={3}
          style={{
            flex: 1,
            minWidth: "300px",
            height: "400px",
            padding: "10px",
            margin: "10px",
            position: "relative",
          }}>
          <Typography variant="h7" align="center" style={{ position: "absolute", width: "300px" }}>
            Toimitettu betoni valmistajan mukaan
          </Typography>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={pieData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={100}
                fill="#8884d8"
                label>
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </Paper>

        <PercentageChangePieChart
          currentVal={selectedMonthData[0]?.keikkaCount}
          comparingVal={comparingMonthData?.keikkaCount / comparingMonthsCount}
          title1={"Tilausten lukumäärä"}
          title2={
            selectedMonthData[0]?.keikkaCount +
            " kpl" +
            " / " +
            Math.round(comparingMonthData?.keikkaCount / comparingMonthsCount) +
            " kpl"
          }
        />
        <PercentageChangePieChart
          currentVal={selectedMonthData[0]?.keikkaMinutes}
          comparingVal={comparingMonthData?.keikkaMinutes / comparingMonthsCount}
          title1="Työmaa tunnit"
          title2={
            Math.round(selectedMonthData[0]?.keikkaMinutes / 6) / 10 +
            " h / " +
            Math.round(comparingMonthData?.keikkaMinutes / comparingMonthsCount / 6) / 10 +
            " h"
          }
        />
        <PercentageChangePieChart
          currentVal={selectedMonthData[0]?.m3}
          comparingVal={comparingMonthData?.m3 / comparingMonthsCount}
          title1="Pumpatut kuutiot"
          title2={
            Math.round(selectedMonthData[0]?.m3) +
            " m3" +
            " / " +
            Math.round(comparingMonthData?.m3 / comparingMonthsCount) +
            " m3"
          }
        />
        <div className="center" style={{ gap: 12 }}>
          <PercentageChangePieChart
            currentVal={selectedMonthData[0]?.asiakasCount}
            comparingVal={comparingMonthData?.asiakasCount / comparingMonthsCount}
            title1="Asiakas lkm"
            title2={
              Math.round(selectedMonthData[0]?.asiakasCount) +
              " kpl" +
              " / " +
              Math.round(comparingMonthData?.asiakasCount / comparingMonthsCount) +
              " kpl"
            }
          />
          <PercentageChangePieChart
            currentVal={selectedMonthData[0]?.tyomaaCount}
            comparingVal={comparingMonthData?.tyomaaCount / comparingMonthsCount}
            title1="Tyomaa lkm"
            title2={
              Math.round(selectedMonthData[0]?.tyomaaCount) +
              " kpl" +
              " / " +
              Math.round(comparingMonthData?.tyomaaCount / comparingMonthsCount) +
              " kpl"
            }
          />
        </div>
        <Paper
          elevation={3}
          style={{ width: "100%", height: "200px", padding: "10px", margin: "10px" }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis yAxisId="left" />
              <YAxis yAxisId="right" orientation="right" />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Line
                activeDot={{ r: 8 }}
                type="monotone"
                dataKey="m3"
                stroke="#8884d8"
                name="Pumpattu määrä (m3)"
                yAxisId="left"
              />
              <Line
                activeDot={{ r: 8 }}
                type="monotone"
                dataKey="keikkaCount"
                stroke="#82ca9d"
                name="Tilausten määrä"
                yAxisId="right"
              />
            </LineChart>
          </ResponsiveContainer>
        </Paper>
      </div>
    </div>
  );
}
